/**
 * Themed Toast Component
 *
 * Theme-aware wrapper for react-native-toast-message.
 * Automatically applies theme colors and typography to toast notifications.
 */

import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Toast from 'react-native-toast-message';
import { useTheme } from '../../contexts/ThemeContext';

export function ThemedToast() {
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const toastConfig = useMemo(
    () => ({
      success: (props: any) => (
        <View
          style={[
            styles.toastBase,
            { backgroundColor: theme.colors.success, borderLeftColor: theme.colors.border },
          ]}
        >
          <Text style={[styles.title, { color: theme.colors.textInverse }]}>{props.text1}</Text>
          {props.text2 && (
            <Text style={[styles.message, { color: theme.colors.textInverse }]}>{props.text2}</Text>
          )}
        </View>
      ),
      error: (props: any) => (
        <View
          style={[
            styles.toastBase,
            { backgroundColor: theme.colors.error, borderLeftColor: theme.colors.border },
          ]}
        >
          <Text style={[styles.title, { color: theme.colors.textInverse }]}>{props.text1}</Text>
          {props.text2 && (
            <Text style={[styles.message, { color: theme.colors.textInverse }]}>{props.text2}</Text>
          )}
        </View>
      ),
      info: (props: any) => (
        <View
          style={[
            styles.toastBase,
            { backgroundColor: theme.colors.primary, borderLeftColor: theme.colors.border },
          ]}
        >
          <Text style={[styles.title, { color: theme.colors.textInverse }]}>{props.text1}</Text>
          {props.text2 && (
            <Text style={[styles.message, { color: theme.colors.textInverse }]}>{props.text2}</Text>
          )}
        </View>
      ),
    }),
    [theme, styles]
  );

  return <Toast config={toastConfig} />;
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    toastBase: {
      height: 'auto',
      width: '100%',
      padding: theme.spacing.md,
      borderTopLeftRadius: theme.radii.lg,
      borderTopRightRadius: theme.radii.lg,
      borderLeftWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    title: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.xs,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    message: {
      fontSize: theme.typography.fontSizeSm,
      lineHeight: theme.typography.fontSizeSm * theme.typography.lineHeightNormal,
    },
  });
}
